import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UsersRound, MailPlus, MoreHorizontal, Search } from 'lucide-react';
import { PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, Pagination, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Badge,
  Input,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  IconButton,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useContacts,
  useOrgAdmins,
  useOrgMe,
  useOrgRole,
  useDeleteContact,
  useSetPrimaryContact,
  useProvisionLogin,
  orgErrorMessage,
  type Contact,
} from '@/api/organization';
import { memberOrgRole, memberRoleTone, accountState } from './member-model';
import { InviteMemberDialog } from './InviteMemberDialog';
import { ORG_BASE } from '../organizations-service';
import { contactRef } from '@/lib/contact-ref';
import styles from './Members.module.css';

const PAGE_SIZE = 100;
const ROLE_ALL = '__all__';
const STATE_ALL = '__all__';

/**
 * Members — the single People surface for the organization. Lists every org
 * contact: those with a login (Active) and directory-only billing/technical
 * contacts (No login), joined with the org-admins list for org role. Row →
 * per-person detail page (opaque UUIDv5 ref) where account lifecycle is managed.
 */
export function MembersPage() {
  const { show } = useToast();
  const { canManage } = useOrgRole();
  const { data: me } = useOrgMe();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>(ROLE_ALL);
  const [stateFilter, setStateFilter] = useState<string>(STATE_ALL);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removing, setRemoving] = useState<Contact | null>(null);

  const { data, isLoading, isError } = useContacts({ page, pageSize: PAGE_SIZE });
  // Org role join — only manage-capable callers may list admins (see useOrgRole).
  const { data: admins = [] } = useOrgAdmins({ enabled: canManage });

  const setPrimary = useSetPrimaryContact({
    onSuccess: () => show({ tone: 'success', title: 'Primary contact updated' }),
    onError: (err) => show({ tone: 'danger', title: orgErrorMessage(err, 'Could not set primary contact') }),
  });

  const remove = useDeleteContact({
    onSuccess: () => {
      show({ tone: 'success', title: 'Member removed' });
      setRemoving(null);
    },
    onError: (err) => show({ tone: 'danger', title: orgErrorMessage(err, 'Could not remove member') }),
  });

  const provision = useProvisionLogin({
    onSuccess: () =>
      show({ tone: 'success', title: 'Account created', description: 'A set-password link was emailed.' }),
    onError: (err) => show({ tone: 'danger', title: orgErrorMessage(err, 'Could not create account') }),
  });

  // One People surface: every org contact — with a login (Active) or directory-
  // only (No login). Filterable by role and account state.
  const people = data?.results ?? [];
  const q = search.trim().toLowerCase();
  const rows = people.filter((c) => {
    if (q && ![c.contact_name, c.contact_email].some((v) => v?.toLowerCase().includes(q))) return false;
    if (roleFilter !== ROLE_ALL && memberOrgRole(c, admins) !== roleFilter) return false;
    if (stateFilter === 'login' && !c.has_login) return false;
    if (stateFilter === 'nologin' && c.has_login) return false;
    return true;
  });

  // Navigate to the detail page using an opaque UUIDv5 ref, never the raw id.
  const openPerson = (id: number) => {
    if (me) navigate(`${ORG_BASE}/members/${contactRef(me.customer_id, id)}`);
  };

  const columns = useMemo<ColumnDef<Contact, unknown>[]>(() => {
    const isSelf = (c: Contact) =>
      me?.user_uuid ? c.user_uuid === me.user_uuid : me?.contact_id === c.contact_id;

    const cols: ColumnDef<Contact, unknown>[] = [
      {
        id: 'person',
        header: 'Person',
        cell: ({ row }) => {
          const c = row.original;
          return (
            <span className={styles.person}>
              <span className={styles.personText}>
                <span className={styles.personName}>{c.contact_name || '—'}</span>
                {c.contact_email && <span className={styles.personEmail}>{c.contact_email}</span>}
              </span>
              {c.is_primary && (
                <Badge tone="primary" appearance="subtle" size="sm">
                  Primary
                </Badge>
              )}
              {isSelf(c) && (
                <Badge tone="info" appearance="subtle" size="sm">
                  You
                </Badge>
              )}
            </span>
          );
        },
      },
      {
        id: 'role',
        header: 'Org role',
        enableSorting: false,
        cell: ({ row }) => {
          const r = memberOrgRole(row.original, admins);
          return (
            <Badge tone={memberRoleTone(r)} appearance="subtle" size="sm">
              {r}
            </Badge>
          );
        },
      },
      {
        id: 'account',
        header: 'Account',
        enableSorting: false,
        cell: ({ row }) => {
          const s = accountState(row.original);
          return <StatusPill tone={s.tone}>{s.label}</StatusPill>;
        },
      },
    ];

    cols.push({
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        const c = row.original;
        return (
          <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton variant="ghost" size="sm" aria-label={`Actions for ${c.contact_name}`}>
                  <MoreHorizontal size={16} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => openPerson(c.contact_id)}>Manage</DropdownMenuItem>
                {canManage && !c.has_login && (
                  <DropdownMenuItem onSelect={() => provision.mutate(c.contact_id)}>
                    Create account
                  </DropdownMenuItem>
                )}
                {canManage && (
                  <DropdownMenuItem
                    disabled={c.is_primary || setPrimary.isPending}
                    onSelect={() => setPrimary.mutate(c.contact_id)}
                  >
                    Set as primary
                  </DropdownMenuItem>
                )}
                {canManage && (
                  <DropdownMenuItem tone="danger" disabled={c.is_primary} onSelect={() => setRemoving(c)}>
                    Remove from org
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admins, canManage, me, setPrimary, provision]);

  const pagination = data?.pagination;

  return (
    <>
      <PageHeader
        icon={<UsersRound size={22} />}
        title="Members"
        description="Everyone in your organization — manage access and accounts."
        actions={
          canManage ? (
            <Button variant="primary" startIcon={<MailPlus size={15} />} onClick={() => setInviteOpen(true)}>
              Invite member
            </Button>
          ) : undefined
        }
      />

      <Toolbar
        start={
          <div className={styles.filters}>
            <div className={styles.searchWrap}>
              <Input
                placeholder="Search members…"
                startSlot={<Search size={15} />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className={styles.filterSelect}>
                <SelectValue placeholder="All roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ROLE_ALL}>All roles</SelectItem>
                <SelectItem value="Owner">Owner</SelectItem>
                <SelectItem value="Admin">Admin</SelectItem>
                <SelectItem value="Member">Member</SelectItem>
              </SelectContent>
            </Select>
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger className={styles.filterSelect}>
                <SelectValue placeholder="All accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={STATE_ALL}>All accounts</SelectItem>
                <SelectItem value="login">Has login</SelectItem>
                <SelectItem value="nologin">No login</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 48 }} />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          icon={<UsersRound size={28} />}
          title="Couldn't load members"
          description="Something went wrong fetching your organization members. Please try again."
        />
      ) : rows.length === 0 ? (
        q || roleFilter !== ROLE_ALL || stateFilter !== STATE_ALL ? (
          <EmptyState icon={<Search size={28} />} title="No matching people" description="Try adjusting your search or filters." />
        ) : (
          <EmptyState
            icon={<UsersRound size={28} />}
            title="No members yet"
            description={canManage ? 'Invite someone to sign in and collaborate.' : 'No members have been added yet.'}
            actions={
              canManage ? (
                <Button startIcon={<MailPlus size={15} />} onClick={() => setInviteOpen(true)}>
                  Invite member
                </Button>
              ) : undefined
            }
          />
        )
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => String(r.contact_id)}
          onRowClick={(r) => openPerson(r.contact_id)}
          footer={
            pagination && pagination.total_pages > 1 ? (
              <Pagination
                page={pagination.page}
                pageCount={pagination.total_pages}
                onPageChange={setPage}
                summary={`Showing ${rows.length} of ${people.length} people`}
              />
            ) : (
              <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                {`${rows.length} ${rows.length === 1 ? 'person' : 'people'}`}
              </span>
            )
          }
        />
      )}

      <InviteMemberDialog open={inviteOpen} onOpenChange={setInviteOpen} />

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Remove member?"
        description={`“${removing?.contact_name ?? 'This member'}” will be removed from your organization. This cannot be undone.`}
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.contact_id)}
      />
    </>
  );
}

export default MembersPage;
